import React from "react";
import classNames from "classnames";
import { useLanguageContext } from "@/hooks";

export type StepItem = {
  step?: number;
  name?: string;
  displayCode?: string;
};

type StepsIndicatorProps = {
  steps: StepItem[];
  activeStep: number;
  className?: string;
};

/**
 * Horizontal step indicator that shows step number and label.
 * Label priority: displayCode (translated) > name > step number.
 */
export default function StepsIndicator({ steps, activeStep, className }: StepsIndicatorProps) {
  const { language } = useLanguageContext();

  return (
    <div className={classNames("d-flex align-items-center gap-3", className)}>
      {steps.map((item, idx) => {
        const stepNumber = item.step ?? idx + 1;
        const label =
          item.displayCode
            ? language(item.displayCode) || item.name || `${stepNumber}`
            : item.name || `${stepNumber}`;

        const isActive = activeStep >= stepNumber;
        const isLast = idx === steps.length - 1;

        return (
          <div key={stepNumber} className="d-flex align-items-center gap-2">
            <div className="d-flex flex-column align-items-center">
              <div
                className={classNames(
                  "rounded-circle d-flex align-items-center justify-content-center",
                  {
                    "bg-success text-white": isActive,
                    "bg-light": !isActive,
                  }
                )}
                style={{ width: 36, height: 36 }}
              >
                {stepNumber}
              </div>
              <small className="text-muted text-center mt-1" style={{ minWidth: 72 }}>
                {label}
              </small>
            </div>
            {!isLast && <div style={{ width: 60, height: 2 }} className="bg-secondary"></div>}
          </div>
        );
      })}
    </div>
  );
}
